import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMinutes } from "@/lib/constants";
import { Building2, Plus, Trash2, FolderClosed, FolderOpen, ChevronRight, Loader2, Server, Clock, PackageCheck, ExternalLink, Zap, Camera, ImageIcon } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";
const IMP_STATUS = ["montato", "in_collaudo", "consegnato", "manutenzione"];
const STATUS_LBL = { montato: "Montato", in_collaudo: "In collaudo", consegnato: "Consegnato", manutenzione: "Manutenzione" };

export default function Impianti() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [tree, setTree] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState({});
  const [newCommittente, setNewCommittente] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [impForm, setImpForm] = useState({ committente_id: "", commessa: "", name: "", code: "", install_date: "", status: "montato", line_id: "", notes: "" });

  const load = async () => {
    const [t, c, l] = await Promise.all([api.get("/impianti/tree"), api.get("/committenti"), api.get("/lines")]);
    setTree(t.data);
    setCommittenti(c.data);
    setLines(l.data);
  };
  useEffect(() => { load(); }, []);

  const addCommittente = async (e) => {
    e.preventDefault();
    if (!newCommittente.trim()) return;
    setBusy(true);
    try { await api.post("/committenti", { name: newCommittente.trim() }); setNewCommittente(""); toast.success("Committente aggiunto."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addImpianto = async (e) => {
    e.preventDefault();
    if (!impForm.committente_id || !impForm.commessa || !impForm.name) return toast.error("Committente, commessa e nome obbligatori.");
    setBusy(true);
    try { await api.post("/impianti", impForm); setImpForm({ committente_id: impForm.committente_id, commessa: "", name: "", code: "", install_date: "", status: "montato", line_id: "", notes: "" }); toast.success("Impianto aggiunto."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const delCommittente = async (id) => { if (!window.confirm("Eliminare il committente e tutti i suoi impianti?")) return; await api.delete(`/committenti/${id}`); toast.success("Committente eliminato."); load(); };
  const delImpianto = async (id) => { if (!window.confirm("Eliminare questo impianto?")) return; await api.delete(`/impianti/${id}`); toast.success("Impianto eliminato."); load(); };

  const uploadPhotos = async (impId, files) => {
    if (!files || files.length === 0) return;
    setUploadingId(impId);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/impianti/${impId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Foto caricata.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadingId(null); }
  };

  const totalImpianti = useMemo(() => tree.reduce((s, c) => s + c.impianti_count, 0), [tree]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Impianti Montati</h1>
        <p className="text-slate-500 text-sm mt-1">{tree.length} committenti · {totalImpianti} impianti, organizzati per committente e commessa.</p>
      </div>

      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form onSubmit={addCommittente} className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="font-heading text-base font-bold mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-400" /> Nuovo committente</h2>
            <div className="flex gap-2">
              <input data-testid="committente-name" className={inputCls} placeholder="Nome committente" value={newCommittente} onChange={(e) => setNewCommittente(e.target.value)} />
              <button type="submit" disabled={busy} data-testid="committente-save" className="h-11 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-1">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</button>
            </div>
          </form>

          <form onSubmit={addImpianto} className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="font-heading text-base font-bold mb-3 flex items-center gap-2"><Server className="h-4 w-4 text-amber-400" /> Nuovo impianto</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select data-testid="impianto-committente" className={inputCls} value={impForm.committente_id} onChange={(e) => setImpForm({ ...impForm, committente_id: e.target.value })}>
                <option value="">Committente…</option>
                {committenti.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input data-testid="impianto-commessa" className={inputCls} placeholder="Commessa (es. COM-2026-100)" value={impForm.commessa} onChange={(e) => setImpForm({ ...impForm, commessa: e.target.value })} />
              <input data-testid="impianto-name" className={inputCls} placeholder="Nome impianto" value={impForm.name} onChange={(e) => setImpForm({ ...impForm, name: e.target.value })} />
              <input data-testid="impianto-code" className={inputCls} placeholder="Codice (opz.)" value={impForm.code} onChange={(e) => setImpForm({ ...impForm, code: e.target.value })} />
              <input data-testid="impianto-date" type="date" className={inputCls} value={impForm.install_date} onChange={(e) => setImpForm({ ...impForm, install_date: e.target.value })} />
              <select data-testid="impianto-status" className={inputCls} value={impForm.status} onChange={(e) => setImpForm({ ...impForm, status: e.target.value })}>
                {IMP_STATUS.map((s) => <option key={s} value={s}>{STATUS_LBL[s]}</option>)}
              </select>
              <select data-testid="impianto-line" className={inputCls} value={impForm.line_id} onChange={(e) => setImpForm({ ...impForm, line_id: e.target.value })}>
                <option value="">Macchinario (per componenti elettrici)…</option>
                {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="mt-3">
              <button type="submit" disabled={busy} data-testid="impianto-save" className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Aggiungi impianto</button>
            </div>
          </form>
        </div>
      )}

      {/* Tree */}
      <div className="space-y-3">
        {tree.length === 0 && <p className="text-slate-500 text-sm">Nessun committente. {isAdmin ? "Aggiungine uno per iniziare." : ""}</p>}
        {tree.map((c) => {
          const isOpen = open[c.id] ?? true;
          return (
            <div key={c.id} data-testid={`committente-${c.id}`} className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-900/80">
                <button onClick={() => setOpen((o) => ({ ...o, [c.id]: !isOpen }))} className="flex items-center gap-2 flex-1 text-left">
                  {isOpen ? <FolderOpen className="h-5 w-5 text-amber-400" /> : <FolderClosed className="h-5 w-5 text-slate-400" />}
                  <span className="font-heading font-bold">{c.name}</span>
                  <span className="hidden sm:flex items-center gap-3 text-xs font-mono-data text-slate-500">
                    <span>{c.impianti_count} impianti · {c.commesse.length} commesse</span>
                    <span className="flex items-center gap-1 text-slate-400"><Clock className="h-3.5 w-3.5" />{formatMinutes(c.total_minutes || 0)}</span>
                    <span className="flex items-center gap-1 text-emerald-400"><PackageCheck className="h-3.5 w-3.5" />{c.delivered_count || 0} consegnati</span>
                  </span>
                </button>
                {isAdmin && <button onClick={() => delCommittente(c.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>}
              </div>
              {isOpen && (
                <div className="px-4 py-3 space-y-3">
                  {c.commesse.length === 0 && <p className="text-slate-600 text-sm pl-2">Nessun impianto.</p>}
                  {c.commesse.map((grp) => (
                    <div key={grp.commessa} className="border-l-2 border-slate-800 pl-4">
                      <div className="flex items-center gap-2 text-sm text-amber-400 font-mono-data mb-2">
                        <ChevronRight className="h-4 w-4" /> {grp.commessa}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {grp.impianti.map((imp) => (
                          <div key={imp.id} data-testid={`impianto-${imp.id}`} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                            <div className="flex items-start justify-between">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{imp.name}</div>
                                <div className="text-xs text-slate-500 font-mono-data">{imp.code || "—"}{imp.install_date ? ` · ${imp.install_date}` : ""}</div>
                              </div>
                              {isAdmin && <button onClick={() => delImpianto(imp.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>}
                            </div>
                            <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{STATUS_LBL[imp.status] || imp.status}</span>
                            <button onClick={() => navigate(`/storico?commessa=${encodeURIComponent(grp.commessa)}`)} data-testid={`impianto-montaggi-${imp.id}`} className="mt-2 ml-2 inline-flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300">
                              <ExternalLink className="h-3 w-3" /> Montaggi
                            </button>
                            {imp.line_name && (
                              <div className="mt-2 pt-2 border-t border-slate-800/70">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1"><Zap className="h-3 w-3 text-amber-400" /> Elettrici · {imp.line_name}</div>
                                {(imp.electrical || []).length === 0 ? (
                                  <div className="text-[11px] text-slate-600">Nessun componente compatibile</div>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {imp.electrical.map((el) => (
                                      <span key={el.id} data-testid={`impianto-elec-${imp.id}-${el.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30" title={`${el.brand} · ${el.codice_prodotto}`}>{el.name}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="mt-2 pt-2 border-t border-slate-800/70">
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Camera className="h-3 w-3 text-amber-400" /> Foto ({(imp.attachments || []).length})</div>
                                <label data-testid={`impianto-photo-upload-${imp.id}`} className="inline-flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 cursor-pointer">
                                  {uploadingId === imp.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Aggiungi
                                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(imp.id, e.target.files)} />
                                </label>
                              </div>
                              {(imp.attachments || []).length === 0 ? (
                                <div className="text-[11px] text-slate-600 flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Nessuna foto</div>
                              ) : (
                                <div className="flex flex-wrap gap-1.5">
                                  {(imp.attachments || []).map((a, idx) => {
                                    const url = `${API}/files/${a.storage_path}`;
                                    return (
                                      <a key={idx} href={url} target="_blank" rel="noreferrer" data-testid={`impianto-photo-${imp.id}-${idx}`} className="block h-14 w-14 rounded-md overflow-hidden border border-slate-700 hover:border-amber-500 transition-colors">
                                        <img src={url} alt={a.original_filename} className="h-full w-full object-cover" />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
